import React, { useEffect, useState } from 'react';
import Papa from 'papaparse';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import './ShuffleTeams.css';

function ShuffleTeams() {
    const [players, setPlayers] = useState([]);
    const [selectedPlayers, setSelectedPlayers] = useState([]);
    const [teams, setTeams] = useState({ teamA: [], teamB: [] });
    const [error, setError] = useState(null);
    const [isAutoMode, setIsAutoMode] = useState(true);

    useEffect(() => {
        fetch('/data/player_statistics.csv')
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response.text();
            })
            .then(csvText => {
                if (csvText.startsWith('<!DOCTYPE html>')) {
                    throw new Error('Received HTML instead of CSV');
                }
                Papa.parse(csvText, {
                    header: true,
                    complete: (result) => {
                        setPlayers(result.data.map(player => {
                            const score = parseFloat(player["Mittelfristiger\nScore\n(Letzten10)"]?.replace(',', '.') || 0);
                            return {
                                name: player["Spieler"],
                                score: isNaN(score) ? 1 : score // Handle NaN values by replacing them with 0
                            };
                        }));
                    }
                });
            })
            .catch(error => {
                setError(error.message);
            });
    }, []);

    const handleSelectPlayer = (player) => {
        setSelectedPlayers(prev => {
            if (prev.includes(player)) {
                return prev.filter(p => p !== player);
            } else {
                return [...prev, player];
            }
        });
    };

    const handleModeChange = (event, newMode) => {
        if (newMode !== null) {
            setIsAutoMode(newMode === 'auto');
            setTeams({ teamA: [], teamB: [] }); // Reset teams when switching modes
            setSelectedPlayers([]); // Reset player selections
        }
    };

    const handleDragStart = (event, player) => {
        event.dataTransfer.setData('player', JSON.stringify(player));
    };

    const handleDrop = (event, team) => {
        event.preventDefault();
        const player = JSON.parse(event.dataTransfer.getData('player'));

        setTeams(prevTeams => {
            const updatedTeams = { ...prevTeams };
            if (!updatedTeams[team].includes(player)) {
                updatedTeams[team] = [...updatedTeams[team], player];
            }
            return updatedTeams;
        });

        setSelectedPlayers(prev => prev.filter(p => p !== player));
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const distributePlayers = (players) => {
        const sortedPlayers = [...players].sort((a, b) => b.score - a.score); // Sort players by score descending
        const teamA = [];
        const teamB = [];
        let totalScoreA = 0;
        let totalScoreB = 0;

        sortedPlayers.forEach((player) => {
            if (totalScoreA <= totalScoreB) {
                teamA.push(player);
                totalScoreA += player.score;
            } else {
                teamB.push(player);
                totalScoreB += player.score;
            }
        });

        setTeams({ teamA, teamB });
    };

    const handleShuffle = () => {
        if (isAutoMode) {
            distributePlayers(selectedPlayers);
        }
    };

    if (error) {
        return <div>Error: {error}</div>;
    }

    return (
        <div className="shuffle-teams">
            <h1>Team Setup</h1>
            <p>Teams können automatisch oder manuell gemischt werden. Bei manueller Auswahl per Drag & Drop.</p>
            <ToggleButtonGroup
                value={isAutoMode ? 'auto' : 'manual'}
                exclusive
                onChange={handleModeChange}
                aria-label="Mode Toggle"
            >
                <ToggleButton value="auto" aria-label="Auto Mode">
                    Auto Modus
                </ToggleButton>
                <ToggleButton value="manual" aria-label="Manual Mode">
                    Manueller Modus
                </ToggleButton>
            </ToggleButtonGroup>
            <button
                onClick={handleShuffle}
                className="shuffle-button"
                disabled={!isAutoMode}
            >
                Shuffle
            </button>
            <div className="player-buttons">
                {players.map((player, index) => (
                    <button
                        key={index}
                        draggable={!isAutoMode}
                        onDragStart={(event) => !isAutoMode && handleDragStart(event, player)}
                        className={selectedPlayers.includes(player) ? 'selected' : ''}
                        onClick={() => handleSelectPlayer(player)}
                    >
                        {player.name} - {player.score}
                    </button>
                ))}
            </div>
            <div className="teams">
                <div
                    className="team-box"
                    onDragOver={handleDragOver}
                    onDrop={(event) => handleDrop(event, 'teamA')}
                >
                    <h2>Team Grün (Total Score: {teams.teamA.reduce((sum, player) => sum + player.score, 0).toFixed(2)})</h2>
                    <ul>
                        {teams.teamA.map((player, index) => (
                            <li key={index}>{player.name} - {player.score}</li>
                        ))}
                    </ul>
                </div>
                <div
                    className="team-box"
                    onDragOver={handleDragOver}
                    onDrop={(event) => handleDrop(event, 'teamB')}
                >
                    <h2>Team Blau (Total Score: {teams.teamB.reduce((sum, player) => sum + player.score, 0).toFixed(2)})</h2>
                    <ul>
                        {teams.teamB.map((player, index) => (
                            <li key={index}>{player.name} - {player.score}</li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}

export default ShuffleTeams;